import React, { useMemo, useState } from 'react'
import { DEALS, dateFromDaysAgo, type Deal } from './crmData'
import { useCrm } from './useCrm'
import { fetchAllDeals, moveDealStage, getAllDemos, type DealDemo } from './crmApi'
import { Card, FunnelRow, IconCalendarUi, InitialBox, StageBadge, Pill, Kpi, SegTabs, SearchInput, crmToast, CrmToaster } from './crmUi'
import { Button } from '../shared/Button'
import { DealDetail } from './DealDetail'
import { LeadRedirect } from './CrmLeadPage'

/**
 * Demo Pipeline — driven by the REAL MEETINGS (Calendly demos table), upcoming and held, joined to
 * their leads; plus the stage-driven cold-lead booking worklist. Card-state on a deal only exists
 * while a demo is upcoming, so this page reads the demos data directly — a demo that happened
 * yesterday still counts here.
 */

const LEAD_STAGES = ['auto_new_lead', 'qualification', 'demo_scheduled']
const TRIAL_STAGES = ['onboarding_in_progress', 'onboarding_complete', 'free_trial_expired', 'trial_expired_contacted', 'trial_reactivated']

type Bucket = 'lead' | 'trial' | 'live' | null
const bucketOf = (d: Deal): Bucket => {
  if (d.stage === 'successfully_signed_up') return 'live'
  if (TRIAL_STAGES.includes(d.stage) || d.trialStartedDays != null) return 'trial'
  if (LEAD_STAGES.includes(d.stage)) return 'lead'
  return null // terminal
}
const hasDemo = (d: Deal) => d.demoScheduledDays != null || d.stage === 'demo_scheduled'
const round = (n: number) => Math.round(n)
const pct = (num: number, den: number) => (den > 0 ? round((num / den) * 100) : 0)
const DAY = 86400000

type DemoRow = DealDemo & { dealId: string | null }

export const CrmDemoPipeline: React.FC = () => {
  const { data: deals, reload } = useCrm(
    () => fetchAllDeals({ includeTerminal: true }) as Promise<Deal[]>,
    DEALS,
  )
  // The meetings themselves — the source of truth for this page.
  const { data: demos, reload: reloadDemos } = useCrm<DemoRow[]>(() => getAllDemos() as Promise<DemoRow[]>, [])
  const [openDeal, setOpenDeal] = useState<Deal | null>(null)
  const [booking, setBooking] = useState<string | null>(null)
  const [tab, setTab] = useState<'scheduled' | 'held' | 'book'>('scheduled')
  // Search across the tab lists — company, contact, invitee, host, event name.
  const [q, setQ] = useState('')

  const dealById = useMemo(() => {
    const m = new Map<string, Deal>()
    deals.forEach((d) => m.set(d.id, d))
    return m
  }, [deals])

  const seg = useMemo(() => {
    const withBucket = deals.map((d) => ({ d, b: bucketOf(d) }))
    const lead = withBucket.filter((x) => x.b === 'lead').map((x) => x.d)
    const trial = withBucket.filter((x) => x.b === 'trial').map((x) => x.d)
    const live = withBucket.filter((x) => x.b === 'live').map((x) => x.d)

    // Meetings, joined to their lead's lifecycle bucket.
    const booked = demos.filter((m) => m.status !== 'cancelled')
    const upcoming = booked.filter((m) => m.upcoming).sort((a, b) => (a.startAt || '').localeCompare(b.startAt || ''))
    const held30 = booked.filter((m) => !m.upcoming && m.startAt && Date.now() - new Date(m.startAt).getTime() < 30 * DAY)
    const bucketOfDemo = (m: DemoRow): Bucket | 'unmatched' => {
      const d = m.dealId ? dealById.get(m.dealId) : undefined
      return d ? bucketOf(d) : 'unmatched'
    }
    return {
      toBook: lead.filter((d) => !hasDemo(d)).sort((a, b) => (a.createdDays ?? 0) - (b.createdDays ?? 0)),
      upcoming,
      upcomingCold: upcoming.filter((m) => bucketOfDemo(m) === 'lead'),
      upcomingTrial: upcoming.filter((m) => bucketOfDemo(m) === 'trial'),
      upcomingLive: upcoming.filter((m) => bucketOfDemo(m) === 'live'),
      unmatched: upcoming.filter((m) => bucketOfDemo(m) === 'unmatched'),
      held30: held30.sort((a, b) => (b.startAt || '').localeCompare(a.startAt || '')),
      heldAll: booked.filter((m) => !m.upcoming).sort((a, b) => (b.startAt || '').localeCompare(a.startAt || '')),
      cancelled30: demos.filter((m) => { if (m.status !== 'cancelled' || !m.startAt) return false; const ago = Date.now() - new Date(m.startAt).getTime(); return ago > -30 * DAY && ago < 30 * DAY }).length,
      coldOnTrial: trial.filter((d) => d.tags?.includes('Cold Lead')),
      leadCount: lead.length,
      trialCount: trial.length,
      liveCount: live.length,
    }
  }, [deals, demos, dealById])

  const goneLiveRate = pct(seg.liveCount, seg.trialCount + seg.liveCount)
  const maxFunnel = Math.max(seg.leadCount, 1)

  /** Booking a demo advances the lead's pipeline stage — the CRM's normal flow. */
  const bookDemo = async (d: Deal) => {
    setBooking(d.id)
    try {
      await moveDealStage(d.id, 'demo_scheduled')
      crmToast(`${d.company || d.name} moved to Demo Scheduled`, 'success')
      reload()
    } catch {
      crmToast('Could not update the lead', 'error')
    } finally {
      setBooking(null)
    }
  }

  return (
    <div className="h-full flex flex-col bg-surface-secondary">
      <CrmToaster />
      <div className="px-6 pt-6 pb-4 bg-surface border-b border-border">
        <h1 className="text-xl font-bold text-txt-primary">Demo Pipeline</h1>
        <p className="text-sm text-txt-secondary">Demos across the lead lifecycle — driven by each lead's pipeline stage. Booking a demo moves the lead to “Demo Scheduled”.</p>
      </div>

      {/* ── FIXED top block — next demo, KPIs, lifecycle. Never scrolls. ── */}
      <div className="px-6 pt-4 pb-3 space-y-3 shrink-0">
        {/* Next upcoming demo only — the full schedule lives in the Scheduled tab. */}
        <Card title="Next demo" subtitle={seg.upcoming.length > 1 ? `${seg.upcoming.length - 1} more upcoming — see Scheduled demos` : undefined}>
          <DemoList demos={seg.upcoming.slice(0, 1)} dealById={dealById} empty="No upcoming demos booked" onOpen={setOpenDeal} />
        </Card>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Upcoming demos" value={seg.upcoming.length} tone="blue" />
          <Kpi label="Held (30d)" value={seg.held30.length} tone="green" />
          <Kpi label="Cancelled (30d)" value={seg.cancelled30} tone="red" warn={seg.cancelled30 > 0} />
          <Kpi label="Cold leads to book" value={seg.toBook.length} tone="amber" warn={seg.toBook.length > 0} />
        </div>

        <Card title="Lifecycle" subtitle="Cold leads → on trial → live (from each lead's stage)">
          <div className="space-y-2">
            <FunnelRow label="Cold leads" barPct={(seg.leadCount / maxFunnel) * 100} color="#2563EB" inner={seg.leadCount} />
            <FunnelRow label="On trial" barPct={(seg.trialCount / maxFunnel) * 100} color="#0EA5E9" inner={seg.trialCount} right={`${pct(seg.trialCount, seg.leadCount + seg.trialCount + seg.liveCount)}% of pipeline`} />
            <FunnelRow label="Live" barPct={(seg.liveCount / maxFunnel) * 100} color="#22C55E" inner={seg.liveCount} right={`${goneLiveRate}% of trials`} />
          </div>
        </Card>

        {/* Tabs — the lists themselves scroll inside a fixed-height container below. */}
        <div className="flex items-center gap-3 flex-wrap">
          <SegTabs
            tabs={[
              { key: 'scheduled', label: `Scheduled demos (${seg.upcoming.length})` },
              { key: 'held', label: `Held demos (${seg.heldAll.length})` },
              { key: 'book', label: `Book a demo (${seg.toBook.length})` },
            ]}
            value={tab}
            onChange={(k) => setTab(k as 'scheduled' | 'held' | 'book')}
          />
          <SearchInput value={q} onChange={setQ} placeholder="Search demos & leads…" className="w-60" />
        </div>
      </div>

      {/* ── Fixed-height tab container — only its CONTENT scrolls. ── */}
      {(() => {
        const needle = q.trim().toLowerCase()
        const demoMatch = (m: DemoRow) => {
          if (!needle) return true
          const d = m.dealId ? dealById.get(m.dealId) : undefined
          return `${d?.company || ''} ${d?.name || ''} ${m.inviteeName || ''} ${m.inviteeEmail || ''} ${m.hostName || ''} ${m.eventName || ''}`.toLowerCase().includes(needle)
        }
        const dealMatch = (d: Deal) => !needle || `${d.company} ${d.name} ${d.contact || ''} ${d.source || ''}`.toLowerCase().includes(needle)
        const fUpcoming = seg.upcoming.filter(demoMatch)
        const fUnmatched = seg.unmatched.filter(demoMatch)
        const fHeld = seg.heldAll.filter(demoMatch)
        const fToBook = seg.toBook.filter(dealMatch)
        return (
      <div className="flex-1 min-h-0 px-6 pb-6">
        <div className="h-full rounded-xl border border-border bg-surface overflow-y-auto p-4">
          {tab === 'scheduled' && (
            <>
              <DemoList demos={fUpcoming} dealById={dealById} empty={needle ? 'No matches' : 'No upcoming demos booked'} onOpen={setOpenDeal} />
              {fUnmatched.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">Unmatched bookings — check by hand</p>
                  <DemoList demos={fUnmatched} dealById={dealById} empty="" onOpen={setOpenDeal} />
                </div>
              )}
            </>
          )}

          {tab === 'held' && (
            <DemoList demos={fHeld} dealById={dealById} empty={needle ? 'No matches' : 'No demos held yet'} onOpen={setOpenDeal} />
          )}

          {tab === 'book' && (
            <>
              {fToBook.length === 0 ? (
                <p className="text-sm text-txt-secondary py-2">Every cold lead has a demo booked</p>
              ) : (
                <div className="space-y-1">
                  {fToBook.map((d) => (
                    <div key={d.id} className="flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-surface-hover border-b border-border last:border-b-0">
                      <button onClick={() => setOpenDeal(d)} className="flex items-center gap-3 min-w-0 flex-1 text-left">
                        <InitialBox name={d.company || d.name} logoUrl={d.logoUrl} />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-txt-primary truncate">{d.company || d.name}</p>
                          <p className="text-xs text-txt-tertiary truncate">{d.contact || d.source} · added {dateFromDaysAgo(d.createdDays)}</p>
                        </div>
                      </button>
                      <StageBadge stageKey={d.stage} />
                      <Button size="sm" loading={booking === d.id} onClick={() => bookDemo(d)}>
                        <IconCalendarUi className="w-3.5 h-3.5" /> Book demo
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {seg.coldOnTrial.length > 0 && (
                <div className="mt-4 pt-3 border-t border-border">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-txt-tertiary mb-2">Cold leads now on trial — good demo candidates</p>
                  <DealList deals={seg.coldOnTrial} empty="None yet" onOpen={setOpenDeal} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
        )
      })()}

      {openDeal && <LeadRedirect deal={openDeal} onDone={() => setOpenDeal(null)} />}
    </div>
  )
}

/** Meeting rows for the pipeline — real demo data: event, time, booker, Meet/transcript links. */
const DemoList: React.FC<{ demos: DemoRow[]; dealById: Map<string, Deal>; empty: string; onOpen: (d: Deal) => void }> = ({ demos, dealById, empty, onOpen }) => {
  if (demos.length === 0) return empty ? <p className="text-sm text-txt-secondary py-2">{empty}</p> : null
  return (
    <div className="space-y-1">
      {demos.map((m) => {
        const deal = m.dealId ? dealById.get(m.dealId) : undefined
        const title = deal ? deal.company || deal.name : m.inviteeName || m.inviteeEmail || 'Unknown booker'
        const when = m.startAt
          ? `${new Date(m.startAt).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })} · ${new Date(m.startAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`
          : 'Unknown time'
        return (
          <div key={m.id} className="flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-surface-hover border-b border-border last:border-b-0">
            <button
              onClick={() => deal && onOpen(deal)}
              disabled={!deal}
              className={`flex items-center gap-3 min-w-0 flex-1 text-left ${deal ? '' : 'cursor-default'}`}
            >
              <InitialBox name={title} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-txt-primary truncate">{title}</p>
                <p className="text-xs text-txt-tertiary truncate">
                  {m.eventName} · {when}
                  {m.inviteeName || m.inviteeEmail ? ` · by ${m.inviteeName || m.inviteeEmail}` : ''}
                </p>
              </div>
            </button>
            {m.upcoming ? <Pill tone="green">Upcoming</Pill> : <Pill tone="gray">Held</Pill>}
            {m.meetUrl && m.upcoming && (
              <a href={m.meetUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs font-semibold text-white px-2.5 py-1.5 rounded-lg" style={{ background: '#2F6FB0' }}>
                Join
              </a>
            )}
            {m.transcriptUrl && !m.upcoming && (
              <a href={m.transcriptUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-xs font-medium text-brand-600 hover:underline">
                Transcript
              </a>
            )}
            {deal && <StageBadge stageKey={deal.stage} />}
          </div>
        )
      })}
    </div>
  )
}

const DealList: React.FC<{ deals: Deal[]; empty: string; onOpen: (d: Deal) => void; showDemo?: boolean }> = ({ deals, empty, onOpen, showDemo }) => {
  if (deals.length === 0) return <p className="text-sm text-txt-secondary py-2">{empty}</p>
  return (
    <div className="space-y-1">
      {deals.map((d) => {
        const future = (d.demoScheduledDays ?? 0) < 0
        return (
          <button
            key={d.id}
            onClick={() => onOpen(d)}
            className="w-full text-left flex items-center gap-3 py-2 px-1 rounded-lg hover:bg-surface-hover border-b border-border last:border-b-0"
          >
            <InitialBox name={d.company || d.name} logoUrl={d.logoUrl} />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-txt-primary truncate">{d.company || d.name}</p>
              <p className="text-xs text-txt-tertiary truncate">{d.contact || d.source}</p>
            </div>
            {showDemo && (
              <Pill tone={future ? 'green' : 'blue'}>
                {d.demoScheduledDays == null ? 'Scheduled' : future ? `in ${Math.abs(d.demoScheduledDays)}d` : dateFromDaysAgo(d.demoScheduledDays)}
              </Pill>
            )}
            <StageBadge stageKey={d.stage} />
          </button>
        )
      })}
    </div>
  )
}
